/**
 * Shared role + avatar helpers for user-facing surfaces
 * (header account menu, My Profile, Admin > Users).
 *
 * Role colors/labels are single-sourced here so the badges look identical
 * everywhere. Plain-English capability summaries explain what each role can
 * do without making the user read the permission matrix.
 */

export type Role = 'owner' | 'admin' | 'accounting' | 'dispatcher' | 'tech' | 'viewer';

export const ROLES: Role[] = ['owner', 'admin', 'accounting', 'dispatcher', 'tech', 'viewer'];

export const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  accounting: 'Accounting',
  dispatcher: 'Dispatcher',
  tech: 'Technician',
  viewer: 'Viewer',
};

export const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  accounting: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  dispatcher: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  tech: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  viewer: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

/** What this role can do, in plain English (shown on My Profile). */
export const ROLE_SUMMARY: Record<string, string> = {
  owner:
    'Full access: manages users and roles, integrations, boards and policies, tenant settings, and the audit log. Can promote other users to any role.',
  admin:
    'Manages the tenant day to day: users and roles, integrations, boards and policies, settings, and the audit log.',
  accounting:
    'Read-only financial visibility: views tickets, time entries, audit log, users, and settings. No write access.',
  dispatcher:
    'Works tickets and the dispatch board: views and assigns tickets, manages scheduling, and uses intelligence tools.',
  tech:
    'Works assigned tickets, views schedules, and uses intelligence tools. No administrative access.',
  viewer:
    'Read-only access to tickets, schedules, and analytics for visibility. No administrative access.',
};

export function initialsFor(name: string | null | undefined, email: string): string {
  const source = name && name.trim() ? name : (email.split('@')[0] ?? email);
  return source
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Avatar({
  name,
  email,
  photoUrl,
  size = 'md',
}: {
  name?: string | null;
  email: string;
  photoUrl?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}) {
  const dims =
    size === 'xl'
      ? 'h-20 w-20 text-2xl'
      : size === 'lg'
        ? 'h-14 w-14 text-base'
        : size === 'sm'
          ? 'h-7 w-7 text-xs'
          : size === 'xs'
            ? 'h-6 w-6 text-[10px]'
            : 'h-9 w-9 text-sm';
  if (photoUrl) {
    return <img src={photoUrl} alt="" className={dims + ' rounded-full object-cover shrink-0'} />;
  }
  return (
    <div
      className={
        dims + ' rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center font-bold text-brand-700 dark:text-brand-300 shrink-0 select-none'
      }
      aria-hidden="true"
    >
      {initialsFor(name, email)}
    </div>
  );
}
